import React, { useMemo } from 'react';
import { Stack, Title, Text, Grid, Accordion, ThemeIcon, Card, Group, Badge, Tabs, Box, Tooltip, ActionIcon, Alert, Button, List } from '@mantine/core';
import { IconBriefcase, IconEye, IconAlertTriangle, IconRefresh, IconListDetails, IconSettings } from '@tabler/icons-react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import Tabla from '../../../../globales/componentes/tabla/tabla';
import type { ICotizacionNBS } from '../../../../modelos/CotizadorNBS/entidades/ICotizacionNBS';
import type { IEquipoAscensor } from '../../../../modelos/CotizadorNBS/entidades/IEquipoAscensor';
import type { IEquipoEscalera } from '../../../../modelos/CotizadorNBS/entidades/IEquipoEscalera';
import type { IResultadosEquipo } from '../../../../modelos/CotizadorNBS/entidades/IResultadosEquipo';
import { MENSAJE_CONFIGURACION_ACTUALIZADA } from '../../../../modelos/CotizadorNBS/entidades/EstadoCotizacionNBS';
import type { IConfiguracionState } from '../../../configuracionCotizadorNbs/interfaces';
import { ConfiguracionCotizacionCV } from '../../controladorVista/configuracionCotizacionCV';
import { textoCapacidadAscensor } from '../../utils/especificacionesAscensor';

const RESULTADOS_VACIOS = new Proxy({} as IResultadosEquipo, {
    get: () => 0
});

/** EVEN() de Excel: redondea alejándose de cero al entero par siguiente. */
const par = (valor: number) => {
    const entero = Math.ceil(Math.abs(valor));
    const parSiguiente = entero % 2 === 0 ? entero : entero + 1;
    return valor < 0 ? -parSiguiente : parSiguiente;
};

/**
 * Comisión de venta por equipo (Excel RESUMEN!H / Q):
 * EVEN(((Flete Total + Importado Total) × TRM + Local Total) × % comisión del tipo).
 * PrecioVentaSinImpuestos del back ya es ((Importado + Flete) × TRM + Local) unitario.
 */
const comisionVentaEquipo = (equipo: IEquipoAscensor | IEquipoEscalera, pctComision: number) =>
    par((equipo.resultados?.PrecioVentaSinImpuestos || 0) * (equipo.Cantidad || 0) * pctComision);

interface PasoResumenVistaProps {
    cotizacion: ICotizacionNBS;
    esSoloLectura?: boolean;
    alGuardarConfiguracion?: (configuracion: IConfiguracionState) => Promise<boolean>;
    alUsarConfiguracionGlobal?: () => void;
    alRecalcularConMatricesVigentes?: () => void;
    setEquipoSeleccionado: (equipo: IEquipoAscensor | IEquipoEscalera | null) => void;
    setCantidadContenedoresModal: (cantidad: number) => void;
    setModalDetalleAbierto: (abierto: boolean) => void;
}

export const PasoResumenVista: React.FC<PasoResumenVistaProps> = ({
    cotizacion,
    esSoloLectura = false,
    alGuardarConfiguracion,
    alUsarConfiguracionGlobal,
    alRecalcularConMatricesVigentes,
    setEquipoSeleccionado,
    setCantidadContenedoresModal,
    setModalDetalleAbierto
}) => {
    const mensajesCalculo = cotizacion.ObservacionesCalculo ?? [];
    // Cambios posteriores al último cálculo. "Usar configuración global" también recalcula (con matrices y equipos
    // actuales); "Recalcular" mantiene la configuración de la cotización, así que solo aparece si hay otro motivo.
    const matricesDesactualizadas = cotizacion.MatricesDesactualizadas ?? [];
    const hayQueRecalcular = cotizacion.RequiereRecalculo === true || matricesDesactualizadas.length > 0;
    const motivosSinAplicar: React.ReactNode[] = [
        ...(cotizacion.ConfiguracionDesactualizada ? [`${MENSAJE_CONFIGURACION_ACTUALIZADA} Esta cotización sigue calculada con la configuración anterior.`] : []),
        ...(cotizacion.RequiereRecalculo ? ['Se modificaron equipos o datos del proyecto.'] : []),
        ...(matricesDesactualizadas.length > 0 ? [<>Se actualizaron matrices: <b>{matricesDesactualizadas.join(', ')}</b>.</>] : [])
    ];
    // Moneda del componente local, márgenes y comisión: COP en Colombia, USD en países en dólares.
    const monedaLocal = cotizacion.MonedaLocal ?? 'COP';
    // Un solo vuelo por cotización (valor de su configuración), si algún equipo es de otras ciudades.
    const llevaVuelo = [...cotizacion.Ascensores, ...cotizacion.Escaleras].some(e => e.OtrasCiudades);
    const valorVuelo = Number(cotizacion.ConfiguracionUsada?.ValorPromedioVuelo ?? 0);
    // Flete marítimo por contenedor (Excel RESUMEN!B7 / K7). Se edita en la pestaña Configuración.
    const fleteMaritimoAscensores = cotizacion.FleteMaritimoAscensores ?? 0;
    const fleteMaritimoEscaleras = cotizacion.FleteMaritimoEscaleras ?? 0;

    // Los resultados del back son unitarios (como la hoja Costos del Excel); las hojas
    // Ascensor/Escalera multiplican por Cantidad para los totales.
    const pctComisionAscensores = cotizacion.ResultadosGlobales?.ComisionPorcentajeAscensores ?? 0;
    const pctComisionEscaleras = cotizacion.ResultadosGlobales?.ComisionPorcentajeEscaleras ?? 0;

    const equiposData = useMemo(() => {
        const fila = (e: IEquipoAscensor | IEquipoEscalera, tipoTabla: 'Ascensor' | 'Escalera', pctComision: number) => {
            const cantidad = e.Cantidad || 0;
            const importadoUnitario = e.resultados?.TotalComponenteImportado || 0; // Costos!BE
            const fleteUnitario = e.resultados?.ValorFleteUnitario || 0;           // Costos!BF
            const localUnitario = e.resultados?.TotalLocal || 0;                   // Costos!BG (COP)
            return {
                Id: e.Id,
                tipoTabla,
                Nombre: e.Nombre || '-',
                ComponenteImportadoUnitario: importadoUnitario,
                ComponenteImportadoTotal: importadoUnitario * cantidad,
                FleteUnitarioUSD: fleteUnitario,
                FleteTotalUSD: fleteUnitario * cantidad,
                ComponenteLocalUnitario: localUnitario,
                ComponenteLocalTotal: localUnitario * cantidad,
                Comision: comisionVentaEquipo(e, pctComision),
                Cantidad: cantidad
            };
        };
        return [
            ...(cotizacion.Ascensores || []).map(a => fila(a, 'Ascensor', pctComisionAscensores)),
            ...(cotizacion.Escaleras || []).map(e => fila(e, 'Escalera', pctComisionEscaleras))
        ];
    }, [cotizacion, pctComisionAscensores, pctComisionEscaleras]);

    const columnasResumen = useMemo<ColumnDef<any>[]>(() => {
        // La moneda va en el valor, no en el título de la columna.
        const enMoneda = (moneda: string) => (info: { getValue: () => unknown }) => {
            const valor = info.getValue();
            return valor !== undefined && valor !== null ? `$ ${(valor as number).toLocaleString('es-CO')} ${moneda}` : '-';
        };
        return [
            { accessorKey: 'tipoTabla', header: 'Tipo Equipo' },
            { accessorKey: 'Nombre', header: 'Nombre' },
            { accessorKey: 'ComponenteImportadoUnitario', header: 'Componente Importado Unitario', cell: enMoneda('USD') },
            { accessorKey: 'ComponenteImportadoTotal', header: 'Componente Importado Total', cell: enMoneda('USD') },
            { accessorKey: 'FleteUnitarioUSD', header: 'Flete Unitario', cell: enMoneda('USD') },
            { accessorKey: 'FleteTotalUSD', header: 'Flete Total', cell: enMoneda('USD') },
            { accessorKey: 'ComponenteLocalUnitario', header: 'Componente Local Unitario', cell: enMoneda(monedaLocal) },
            { accessorKey: 'ComponenteLocalTotal', header: 'Componente Local Total', cell: enMoneda(monedaLocal) },
            { accessorKey: 'Comision', header: 'Comisión Estimada', cell: enMoneda(monedaLocal) },
        ];
    }, [monedaLocal]);

    const tablaResumen = useReactTable({
        data: equiposData,
        columns: columnasResumen,
        getCoreRowModel: getCoreRowModel()
    });

    const columnasResumenAscensores = useMemo<ColumnDef<IEquipoAscensor>[]>(() => [
        {
            id: 'actions',
            header: 'Acciones',
            cell: ({ row }) => (
                <Tooltip label="Ver Desglose Matemático" withArrow position="top">
                    <ActionIcon variant="light" color="primary" onClick={() => { setEquipoSeleccionado({ ...row.original, resultados: row.original.resultados ?? RESULTADOS_VACIOS }); setCantidadContenedoresModal((row.original.resultados?.CantidadContenedoresManual || 0) > 0 ? row.original.resultados!.CantidadContenedoresManual! : row.original.resultados?.CantidadContenedoresCalculada ?? 0); setModalDetalleAbierto(true); }}>
                        <IconEye size={18} />
                    </ActionIcon>
                </Tooltip>
            )
        },
        { accessorKey: 'Nombre', header: 'Nombre' },
        { id: 'modelo', header: 'Modelo', accessorFn: (e) => e.ProductoNombre ?? '-' },
        { id: 'capacidad', header: 'Capacidad', accessorFn: (a) => textoCapacidadAscensor(a.Capacidad, a.Pasajeros) },
        { accessorKey: 'VelocidadIngreso', header: 'Velocidad', cell: (info) => `${info.getValue() || '-'} m/s` },
        { accessorKey: 'CostoFCA', header: 'Costo Equipo', cell: (info) => `$ ${info.getValue() !== undefined && info.getValue() !== null ? (info.getValue() as number).toLocaleString('es-CO') : '-'} USD` },
        { id: 'comision', header: 'Comisión', accessorFn: (a) => comisionVentaEquipo(a, pctComisionAscensores), cell: (info) => `$ ${(info.getValue() as number).toLocaleString('es-CO')} ${monedaLocal}` }
    ], [setEquipoSeleccionado, setCantidadContenedoresModal, setModalDetalleAbierto, pctComisionAscensores, monedaLocal]);

    const tablaResumenAscensores = useReactTable({
        data: cotizacion.Ascensores,
        columns: columnasResumenAscensores,
        getCoreRowModel: getCoreRowModel()
    });

    const columnasResumenEscaleras = useMemo<ColumnDef<IEquipoEscalera>[]>(() => [
        {
            id: 'actions',
            header: 'Acciones',
            cell: ({ row }) => (
                <Tooltip label="Ver Desglose Matemático" withArrow position="top">
                    <ActionIcon variant="light" color="primary" onClick={() => { setEquipoSeleccionado({ ...row.original, resultados: row.original.resultados ?? RESULTADOS_VACIOS }); setCantidadContenedoresModal(row.original.resultados?.CantidadContenedoresCalculada ?? (row.original as IEquipoEscalera).Contenedores ?? 0); setModalDetalleAbierto(true); }}>
                        <IconEye size={18} />
                    </ActionIcon>
                </Tooltip>
            )
        },
        { accessorKey: 'Nombre', header: 'Nombre' },
        { id: 'modelo', header: 'Modelo', accessorFn: (e) => e.ProductoNombre ?? '-' },
        { accessorKey: 'Altura', header: 'Altura', cell: (info) => `${info.getValue() || '-'} mm` },
        { accessorKey: 'VelocidadIngreso', header: 'Velocidad', cell: (info) => `${info.getValue() || '-'} m/s` },
        { accessorKey: 'CostoFCA', header: 'Costo Equipo', cell: (info) => `$ ${info.getValue() !== undefined && info.getValue() !== null ? (info.getValue() as number).toLocaleString('es-CO') : '-'} USD` },
        { id: 'comision', header: 'Comisión', accessorFn: (e) => comisionVentaEquipo(e, pctComisionEscaleras), cell: (info) => `$ ${(info.getValue() as number).toLocaleString('es-CO')} ${monedaLocal}` }
    ], [setEquipoSeleccionado, setCantidadContenedoresModal, setModalDetalleAbierto, pctComisionEscaleras, monedaLocal]);

    const tablaResumenEscaleras = useReactTable({
        data: cotizacion.Escaleras,
        columns: columnasResumenEscaleras,
        getCoreRowModel: getCoreRowModel()
    });

    return (
        <Tabs defaultValue="resumen" keepMounted={false}>
            {/* Pestañas fijas (sticky dentro del contenedor con scroll del paso), con el mismo estilo que las
                del módulo de Configuración. Sin título: el stepper ya indica el paso (Resumen · Verificación). */}
            <Box
                mb="md"
                style={{ position: 'sticky', top: 0, zIndex: 5, backgroundColor: 'var(--mantine-color-body)' }}
            >
                <Tabs.List>
                    <Tabs.Tab value="resumen" leftSection={<IconListDetails size={14} />}>Resumen</Tabs.Tab>
                    <Tabs.Tab value="configuracion" leftSection={<IconSettings size={14} />}>Configuración</Tabs.Tab>
                </Tabs.List>
            </Box>

            {/* Observaciones del último cálculo (guardadas en la cotización): qué falta y dónde corregirlo. */}
            {mensajesCalculo.length > 0 && !esSoloLectura && (
                <Alert color="#e6a23c" variant="light" radius="md" py="xs" mb="md" icon={<IconAlertTriangle size={18} />}
                    title={mensajesCalculo.length === 1 ? "Observación del cálculo" : `Observaciones del cálculo (${mensajesCalculo.length})`}>
                    <List size="sm" spacing={4}>
                        {mensajesCalculo.map((mensaje, i) => <List.Item key={i}>{mensaje}</List.Item>)}
                    </List>
                </Alert>
            )}

            {/* Un solo aviso con todo lo que cambió después del último cálculo (configuración global, equipos o
                proyecto, matrices) y una sola vez cada acción. La cotización nunca se recalcula sola. */}
            {motivosSinAplicar.length > 0 && !esSoloLectura && (
                <Alert color="#e6a23c" variant="light" radius="md" py="xs" mb="md" icon={<IconAlertTriangle size={18} />}>
                    <Group justify="space-between" align="center" wrap="wrap" gap="xs">
                        <Box style={{ flex: 1, minWidth: 240 }}>
                            {motivosSinAplicar.length === 1 ? (
                                <Text size="sm">{motivosSinAplicar[0]}</Text>
                            ) : (
                                <>
                                    <Text size="sm" fw={600} mb={4}>Hay cambios después del último cálculo:</Text>
                                    <List size="sm" spacing={2}>
                                        {motivosSinAplicar.map((motivo, i) => <List.Item key={i}>{motivo}</List.Item>)}
                                    </List>
                                </>
                            )}
                            <Text size="sm" mt={4}>Los valores mostrados no incluyen estos cambios.</Text>
                        </Box>
                        <Group gap="xs">
                            {hayQueRecalcular && alRecalcularConMatricesVigentes && (
                                <Tooltip multiline w={260} withArrow withinPortal openDelay={100} zIndex={100000} style={{ fontSize: '11px' }}
                                    label="Recalcula con la configuración que ya tiene la cotización, aplicando las matrices actualizadas y los cambios en equipos o proyecto.">
                                    <Button variant="light" color="primary" size="xs" radius="xl" leftSection={<IconRefresh size={14} />} onClick={alRecalcularConMatricesVigentes}>
                                        Recalcular
                                    </Button>
                                </Tooltip>
                            )}
                            {cotizacion.ConfiguracionDesactualizada && alUsarConfiguracionGlobal && (
                                <Tooltip multiline w={260} withArrow withinPortal openDelay={100} zIndex={100000} style={{ fontSize: '11px' }}
                                    label="Cambia a la configuración global vigente y recalcula (también con las matrices y equipos actuales). Los márgenes, descuentos y flete propios de la cotización se reemplazan por los globales.">
                                    <Button variant="light" color="primary" size="xs" radius="xl" leftSection={<IconRefresh size={14} />} onClick={alUsarConfiguracionGlobal}>
                                        Usar configuración global
                                    </Button>
                                </Tooltip>
                            )}
                        </Group>
                    </Group>
                </Alert>
            )}

            <Tabs.Panel value="configuracion">
                {alGuardarConfiguracion && alUsarConfiguracionGlobal && (
                    <ConfiguracionCotizacionCV
                        cotizacion={cotizacion}
                        soloLectura={esSoloLectura}
                        alGuardar={alGuardarConfiguracion}
                        alUsarConfiguracionGlobal={alUsarConfiguracionGlobal}
                    />
                )}
            </Tabs.Panel>

            <Tabs.Panel value="resumen">
                <Grid>
                    {/* FILA 1: INFORMATIVOS EN ACORDEÓN */}
                    <Grid.Col span={12}>
                        <Accordion variant="separated" radius="md">
                            <Accordion.Item value="parametros-base">
                                <Accordion.Control icon={<ThemeIcon variant="light" size="lg" color="primary"><IconBriefcase size={20} /></ThemeIcon>}>
                                    <Text fw={600} size="md">Ver Parámetros y Costos Base</Text>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    <Grid>
                                        <Grid.Col span={{ base: 12, md: 4 }}>
                                            <Card withBorder radius="md" padding={0} h="100%">
                                                <Card.Section bg="var(--mantine-color-primary-light)" py="xs" px="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                                                    <Title order={6} c="primary">Administración de Obra</Title>
                                                </Card.Section>
                                                <Stack p="md" gap="xs">
                                                    <Group justify="space-between" mb={4} pb={4} style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                                                        <Text size="xs" fw={700} c="dimmed" tt="uppercase">Item</Text>
                                                        <Text size="xs" fw={700} c="dimmed" tt="uppercase">Precio Mensual</Text>
                                                    </Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Profesional SST:</Text><Text size="sm" fw={500}>$ {(cotizacion.AdministracionObra?.ProfesionalSST || 3875000).toLocaleString('es-CO')} USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Ing. Residente:</Text><Text size="sm" fw={500}>$ {(cotizacion.AdministracionObra?.IngResidente || 5425000).toLocaleString('es-CO')} USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Director de Obra:</Text><Text size="sm" fw={500}>$ {(cotizacion.AdministracionObra?.DirectorObra || 6975000).toLocaleString('es-CO')} USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Supervisor de Montaje:</Text><Text size="sm" fw={500}>$ {(cotizacion.AdministracionObra?.SupervisorMontaje || 4650000).toLocaleString('es-CO')} USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Profesional Eléctrico RETIE:</Text><Text size="sm" fw={500}>$ {(cotizacion.AdministracionObra?.ProfesionalElectricoRETIE || 6200000).toLocaleString('es-CO')} USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Ambiental (Aseo obra):</Text><Text size="sm" fw={500}>$ {(cotizacion.AdministracionObra?.AseoObra || 1550000).toLocaleString('es-CO')} USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Oficina BIM:</Text><Text size="sm" fw={500}>$ {(cotizacion.AdministracionObra?.OficinaBIM || 12000000).toLocaleString('es-CO')} USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Soporte KONE MX:</Text><Text size="sm" fw={500}>$ {(cotizacion.AdministracionObra?.SoporteKONEMX || 10850000).toLocaleString('es-CO')} USD</Text></Group>
                                                </Stack>
                                            </Card>
                                        </Grid.Col>
                                        <Grid.Col span={{ base: 12, md: 4 }}>
                                            <Card withBorder radius="md" padding={0} h="100%">
                                                <Card.Section bg="var(--mantine-color-primary-light)" py="xs" px="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                                                    <Title order={6} c="primary">Licitaciones</Title>
                                                </Card.Section>
                                                <Stack p="md" gap="xs">
                                                    <Group justify="space-between" mb={4} pb={4} style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                                                        <Text size="xs" fw={700} c="dimmed" tt="uppercase">Item</Text>
                                                        <Text size="xs" fw={700} c="dimmed" tt="uppercase">Precio</Text>
                                                    </Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Andamios certificados:</Text><Text size="sm" fw={500}>$ 0 USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Equipos de rescate:</Text><Text size="sm" fw={500}>$ 0 USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Cerramientos de entradas:</Text><Text size="sm" fw={500}>$ 0 USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Señalización:</Text><Text size="sm" fw={500}>$ 0 USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Iluminación:</Text><Text size="sm" fw={500}>$ 0 USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Licencia de Software:</Text><Text size="sm" fw={500}>$ 0 USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Intercomunicador de obra:</Text><Text size="sm" fw={500}>$ 0 USD</Text></Group>
                                                </Stack>
                                            </Card>
                                        </Grid.Col>
                                        <Grid.Col span={{ base: 12, md: 4 }}>
                                            <Card withBorder radius="md" padding={0} h="100%">
                                                <Card.Section bg="var(--mantine-color-primary-light)" py="xs" px="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                                                    <Title order={6} c="primary">Logística de Obra</Title>
                                                </Card.Section>
                                                <Stack p="md" gap="xs">
                                                    <Group justify="space-between" mb={4} pb={4} style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                                                        <Text size="xs" fw={700} c="dimmed" tt="uppercase">Item</Text>
                                                        <Text size="xs" fw={700} c="dimmed" tt="uppercase">Precio</Text>
                                                    </Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Residuos Certificados:</Text><Text size="sm" fw={500}>$ 1.000.000 USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Sistema Vigilancia / Baño:</Text><Text size="sm" fw={500}>$ 4.300.000 USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Container / Almacén:</Text><Text size="sm" fw={500}>$ 1.500.000 USD</Text></Group>
                                                    <Group justify="space-between"><Text size="sm" c="dimmed">Descargue nocturno:</Text><Text size="sm" fw={500}>$ 4.240.000 USD</Text></Group>
                                                </Stack>
                                            </Card>
                                        </Grid.Col>
                                    </Grid>
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    </Grid.Col>

                    {/* FILA 2: DATOS DEL PROYECTO Y RESUMEN COMERCIAL */}
                    <Grid.Col span={{ base: 12, md: 4 }}>
                        <Card withBorder radius="md" padding={0} h="100%">
                            <Card.Section bg="var(--mantine-color-primary-light)" py="xs" px="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                                <Title order={6} c="primary">Datos del Proyecto</Title>
                            </Card.Section>
                            <Stack p="md" gap="xs">
                                <Group justify="space-between"><Text size="sm" c="dimmed">Cliente:</Text><Text size="sm" fw={500}>{cotizacion.Proyecto.NombreCliente || '-'}</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Nombre:</Text><Text size="sm" fw={500}>{cotizacion.Proyecto.Nombre || 'Sin Nombre'}</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Ciudad:</Text><Text size="sm" fw={500}>{cotizacion.Proyecto.Ciudad || '-'}</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Año:</Text><Text size="sm" fw={500}>{cotizacion.Proyecto.Anio}</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Duración Contrato:</Text><Text size="sm" fw={500}>{cotizacion.Proyecto.MesesContrato} Meses</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Mantenimiento:</Text><Text size="sm" fw={500}>{cotizacion.Proyecto.MesesMantenimiento} Meses</Text></Group>
                                {llevaVuelo && (
                                    <Group justify="space-between"><Text size="sm" c="dimmed">Vuelos:</Text><Text size="sm" fw={500}>$ {valorVuelo.toLocaleString('es-CO')} {monedaLocal}</Text></Group>
                                )}
                            </Stack>
                        </Card>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 4 }}>
                        <Card withBorder radius="md" padding={0} h="100%">
                            <Card.Section bg="var(--mantine-color-primary-light)" py="xs" px="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                                <Title order={6} c="primary">Resumen de Ascensores</Title>
                            </Card.Section>
                            <Stack p="md" gap="xs">
                                <Group justify="space-between"><Text size="sm" c="dimmed">Descuento KONE:</Text><Text size="sm" fw={500}>{cotizacion.DescuentoAscensores ?? 0}%</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Margen Importado:</Text><Text size="sm" fw={500}>{cotizacion.MargenImportadoAscensores ?? 0}%</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Margen Local:</Text><Text size="sm" fw={500}>{cotizacion.MargenLocalAscensores ?? 0}%</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Flete Marítimo:</Text><Text size="sm" fw={500}>$ {fleteMaritimoAscensores.toLocaleString('es-CO')} USD</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">M. Imp. Venta:</Text><Text size="sm" fw={500}>{((cotizacion.ResultadosGlobales?.MargenImportadoVentaAscensores ?? 0) * 100).toFixed(2)}%</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">M. Local Venta:</Text><Text size="sm" fw={500}>{((cotizacion.ResultadosGlobales?.MargenLocalVentaAscensores ?? 0) * 100).toFixed(2)}%</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Margen Prom:</Text><Text size="sm" fw={500}>{((cotizacion.ResultadosGlobales?.MargenPromedioAscensores ?? 0) * 100).toFixed(2)}%</Text></Group>
                                <Group justify="space-between">
                                    <Text size="sm" c="dimmed" fw={700}>Comisión:</Text>
                                    <Badge color="#40c057" variant="filled" size="lg" fz="sm">{((cotizacion.ResultadosGlobales?.ComisionPorcentajeAscensores ?? 0) * 100).toFixed(2)}%</Badge>
                                </Group>
                            </Stack>
                        </Card>
                    </Grid.Col>

                    <Grid.Col span={{ base: 12, md: 4 }}>
                        <Card withBorder radius="md" padding={0} h="100%">
                            <Card.Section bg="var(--mantine-color-primary-light)" py="xs" px="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                                <Title order={6} c="primary">Resumen de Escaleras</Title>
                            </Card.Section>
                            <Stack p="md" gap="xs">
                                <Group justify="space-between"><Text size="sm" c="dimmed">Descuento KONE:</Text><Text size="sm" fw={500}>{cotizacion.DescuentoEscaleras ?? 0}%</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Margen Importado:</Text><Text size="sm" fw={500}>{cotizacion.MargenImportadoEscaleras ?? 0}%</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Margen Local:</Text><Text size="sm" fw={500}>{cotizacion.MargenLocalEscaleras ?? 0}%</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Flete Marítimo:</Text><Text size="sm" fw={500}>$ {fleteMaritimoEscaleras.toLocaleString('es-CO')} USD</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">M. Imp. Venta:</Text><Text size="sm" fw={500}>{((cotizacion.ResultadosGlobales?.MargenImportadoVentaEscaleras ?? 0) * 100).toFixed(2)}%</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">M. Local Venta:</Text><Text size="sm" fw={500}>{((cotizacion.ResultadosGlobales?.MargenLocalVentaEscaleras ?? 0) * 100).toFixed(2)}%</Text></Group>
                                <Group justify="space-between"><Text size="sm" c="dimmed">Margen Prom:</Text><Text size="sm" fw={500}>{((cotizacion.ResultadosGlobales?.MargenPromedioEscaleras ?? 0) * 100).toFixed(2)}%</Text></Group>
                                <Group justify="space-between">
                                    <Text size="sm" c="dimmed" fw={700}>Comisión:</Text>
                                    <Badge color="#40c057" variant="filled" size="lg" fz="sm">{((cotizacion.ResultadosGlobales?.ComisionPorcentajeEscaleras ?? 0) * 100).toFixed(2)}%</Badge>
                                </Group>
                            </Stack>
                        </Card>
                    </Grid.Col>

                    <Grid.Col span={12}>
                        <Card withBorder radius="md" padding={0} h="100%">
                            <Card.Section bg="var(--mantine-color-primary-light)" py="xs" px="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                                <Group justify="space-between">
                                    <Title order={6} c="primary">Resumen de Equipos</Title>
                                    <Group gap="xs">
                                        <Text size="sm" c="dimmed">Total Equipos:</Text>
                                        <Badge color="primary" variant="filled">
                                            {[...cotizacion.Ascensores, ...cotizacion.Escaleras].reduce((total, e) => total + (e.Cantidad || 0), 0)}
                                        </Badge>
                                    </Group>
                                </Group>
                            </Card.Section>

                            <Box p="md">
                                <Tabs defaultValue={cotizacion.Ascensores.length === 0 && cotizacion.Escaleras.length > 0 ? 'Escaleras' : 'Ascensores'}>
                                    <Tabs.List mb="md">
                                        <Tabs.Tab value="Ascensores">Ascensores ({cotizacion.Ascensores.length})</Tabs.Tab>
                                        <Tabs.Tab value="Escaleras">Escaleras ({cotizacion.Escaleras.length})</Tabs.Tab>
                                    </Tabs.List>

                                    <Tabs.Panel value="Ascensores">
                                        {cotizacion.Ascensores.length === 0 ? (
                                            <Text size="sm" c="dimmed" ta="center" py="xl">No hay Ascensores agregados.</Text>
                                        ) : (
                                            <div style={{ maxHeight: '300px' }}>
                                                <Tabla tabla={tablaResumenAscensores} />
                                            </div>
                                        )}
                                    </Tabs.Panel>

                                    <Tabs.Panel value="Escaleras">
                                        {cotizacion.Escaleras.length === 0 ? (
                                            <Text size="sm" c="dimmed" ta="center" py="xl">No hay Escaleras agregadas.</Text>
                                        ) : (
                                            <div style={{ maxHeight: '300px' }}>
                                                <Tabla tabla={tablaResumenEscaleras} />
                                            </div>
                                        )}
                                    </Tabs.Panel>
                                </Tabs>
                            </Box>
                        </Card>
                    </Grid.Col>

                    <Grid.Col span={12}>
                        <Card withBorder radius="md" padding="md" shadow="sm" style={{ borderLeft: '4px solid var(--mantine-color-primary-filled)' }}>
                            <Title order={5} mb="md" c="primary">Cálculos de Precios y Costos (Sin impuestos)</Title>
                            <div style={{ overflowX: 'auto' }}>
                                <Tabla tabla={tablaResumen} />
                            </div>
                        </Card>
                    </Grid.Col>

                    <Grid.Col span={12}>
                        <Card withBorder radius="md" padding="md" mb="md" shadow="sm">
                            <Title order={4} c="primary" mb="md">Totalización Global del Proyecto</Title>
                            <Grid>
                                <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
                                    <Card withBorder radius="sm" padding="sm">
                                        <Stack gap={4}>
                                            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Precio de Venta</Text>
                                            <Text size="md" fw={700} c="primary">$ {(cotizacion.ResultadosGlobales?.PrecioVentaTotal || 0).toLocaleString('es-CO')} USD</Text>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
                                    <Card withBorder radius="sm" padding="sm">
                                        <Stack gap={4}>
                                            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Componente Importado</Text>
                                            <Text size="lg" fw={700}>$ {(cotizacion.ResultadosGlobales?.CostoImportadoTotal || 0).toLocaleString('es-CO')} USD</Text>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
                                    <Card withBorder radius="sm" padding="sm">
                                        <Stack gap={4}>
                                            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Componente Local</Text>
                                            <Text size="lg" fw={700}>$ {(cotizacion.ResultadosGlobales?.CostoLocalTotal || 0).toLocaleString('es-CO')} {monedaLocal}</Text>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
                                    <Card withBorder radius="sm" padding="sm">
                                        <Stack gap={4}>
                                            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Margen Prom.</Text>
                                            <Text size="lg" fw={700}>{((cotizacion.ResultadosGlobales?.MargenPromedioPorcentaje ?? 0) * 100).toFixed(2)}%</Text>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
                                    <Card withBorder radius="sm" padding="sm" bg="green.0" style={{ borderColor: 'var(--mantine-color-green-3)' }}>
                                        <Stack gap={4}>
                                            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Comisión Total</Text>
                                            <Badge color="#40c057" variant="filled" size="lg" radius="md">$ {(cotizacion.ResultadosGlobales?.ComisionTotal || 0).toLocaleString('es-CO')} {monedaLocal}</Badge>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                            </Grid>
                        </Card>
                    </Grid.Col>
                </Grid>
            </Tabs.Panel>
        </Tabs>
    );
};
